/**
 * @module Controlled state controller for the Tiptap prompt editor.
 *
 * Ported from t3code's composer rich-text layer (ticket 3526, upstream pin
 * e3e7cc3f, MIT license retained in LICENSE), merged with Forge's draft-store
 * contract: the snapshot the ChatComposer reads, the controlled rewrite for
 * store-driven prompt changes, and the focus helpers.
 */

import type { Editor } from "@tiptap/react";
import type { RefObject } from "react";

import {
  clampCollapsedComposerCursor,
  expandCollapsedComposerCursor,
  isCollapsedCursorAdjacentToInlineToken,
} from "~/composer-logic";
import {
  collectComposerTerminalContextIds,
  serializeEditorDoc,
} from "~/composer-rich-text-serialize";
import {
  collapsedToFlat,
  flatToCollapsed,
  flatToMarkdown,
  flatToPm,
  pmToFlat,
} from "~/composer-rich-text-cursor";

export interface ComposerSnapshot {
  value: string;
  cursor: number;
  expandedCursor: number;
  terminalContextIds: string[];
}

export interface ComposerPromptEditorController {
  readonly snapshotRef: RefObject<ComposerSnapshot>;
  readonly selectionRangeRef: RefObject<{ start: number; end: number }>;
  /** Doc or selection change pump; the same callback feeds both editor events. */
  readonly syncFromEditor: (editor: Editor) => void;
  /** Fresh snapshot straight from the live editor document. */
  readonly readSnapshot: (editor: Editor | null) => ComposerSnapshot;
  /** Apply a store-driven value and cursor onto the editor. */
  readonly applyControlled: (editor: Editor, value: string, cursor: number) => void;
  /**
   * Replace the whole document for a store-driven rewrite. setContent with
   * emitUpdate:false still emits selectionUpdate (Tiptap gates only the
   * update event), and the replacement maps the caret to the end of the new
   * document, so the replacement runs guarded like applyControlled.
   */
  readonly rebuild: (editor: Editor, content: Record<string, unknown>) => void;
  /** Move the caret to a collapsed offset and report the change upward. */
  readonly focusAt: (editor: Editor, nextCursor: number) => void;
}

/** Coordinates editor snapshots, controlled draft updates, and cursor placement. */
export function createComposerPromptEditorController(input: {
  readonly initialValue: string;
  readonly initialCursor: number;
  readonly initialTerminalContextIds: ReadonlyArray<string>;
  readonly onChange: (
    nextValue: string,
    nextCursor: number,
    expandedCursor: number,
    cursorAdjacentToMention: boolean,
    terminalContextIds: string[],
  ) => void;
  readonly setIsEmpty: (empty: boolean) => void;
  /** Latest store value, so stale editor snapshots never clobber a rewrite. */
  readonly latestValue: () => string;
}): ComposerPromptEditorController {
  const initialCursor = clampCollapsedComposerCursor(input.initialValue, input.initialCursor);
  const snapshotRef: { current: ComposerSnapshot } = {
    current: {
      value: input.initialValue,
      cursor: initialCursor,
      expandedCursor: expandCollapsedComposerCursor(input.initialValue, initialCursor),
      terminalContextIds: [...input.initialTerminalContextIds],
    },
  };
  const selectionRangeRef: { current: { start: number; end: number } } = {
    current: { start: initialCursor, end: initialCursor },
  };
  const isApplyingControlledUpdate = { current: false };

  // fallow-ignore-next-line complexity -- ported upstream t3code algorithm (ticket 3526); behavior pinned by the ported spec
  const syncFromEditor = (editor: Editor): void => {
    const map = serializeEditorDoc(editor.state.doc);
    const { from, to } = editor.state.selection;
    const fromFlat = pmToFlat(map, from);
    const toFlat = pmToFlat(map, to);
    const nextValue = map.value;
    const nextCursor = clampCollapsedComposerCursor(map.value, flatToCollapsed(map, fromFlat));
    const nextExpandedCursor = Math.max(
      0,
      Math.min(map.value.length, flatToMarkdown(map, fromFlat)),
    );
    selectionRangeRef.current = {
      start: Math.min(nextExpandedCursor, flatToMarkdown(map, toFlat)),
      end: Math.max(nextExpandedCursor, flatToMarkdown(map, toFlat)),
    };
    const nextTerminalContextIds = collectComposerTerminalContextIds(editor.state.doc);
    input.setIsEmpty(nextValue.length === 0);
    const previous = snapshotRef.current;
    if (isApplyingControlledUpdate.current) return;
    if (
      previous.value === nextValue &&
      previous.cursor === nextCursor &&
      previous.expandedCursor === nextExpandedCursor &&
      previous.terminalContextIds.length === nextTerminalContextIds.length &&
      previous.terminalContextIds.every((id, index) => id === nextTerminalContextIds[index])
    ) {
      return;
    }
    if (previous.value === nextValue && nextValue !== input.latestValue()) {
      // A selection-only update while a newer prompt waits to be applied: let
      // the controlled rewrite land instead of reporting stale text.
      return;
    }
    snapshotRef.current = {
      value: nextValue,
      cursor: nextCursor,
      expandedCursor: nextExpandedCursor,
      terminalContextIds: nextTerminalContextIds,
    };
    const cursorAdjacentToMention =
      isCollapsedCursorAdjacentToInlineToken(nextValue, nextCursor, "left") ||
      isCollapsedCursorAdjacentToInlineToken(nextValue, nextCursor, "right");
    input.onChange(
      nextValue,
      nextCursor,
      nextExpandedCursor,
      cursorAdjacentToMention,
      nextTerminalContextIds,
    );
  };

  const readSnapshot = (editor: Editor | null): ComposerSnapshot => {
    const snapshot = snapshotRef.current;
    if (!editor) return snapshot;
    const map = serializeEditorDoc(editor.state.doc);
    const { from, to } = editor.state.selection;
    const fromFlat = pmToFlat(map, from);
    const toFlat = pmToFlat(map, to);
    const next: ComposerSnapshot = {
      value: map.value,
      cursor: clampCollapsedComposerCursor(map.value, flatToCollapsed(map, fromFlat)),
      expandedCursor: Math.max(0, Math.min(map.value.length, flatToMarkdown(map, fromFlat))),
      terminalContextIds: collectComposerTerminalContextIds(editor.state.doc),
    };
    selectionRangeRef.current = {
      start: Math.min(next.expandedCursor, flatToMarkdown(map, toFlat)),
      end: Math.max(next.expandedCursor, flatToMarkdown(map, toFlat)),
    };
    snapshotRef.current = next;
    return next;
  };

  const applyControlled = (editor: Editor, value: string, cursor: number): void => {
    const normalizedCursor = clampCollapsedComposerCursor(value, cursor);
    const previous = snapshotRef.current;
    const normalizedExpandedCursor = expandCollapsedComposerCursor(value, normalizedCursor);
    snapshotRef.current = {
      value,
      cursor: normalizedCursor,
      expandedCursor: normalizedExpandedCursor,
      terminalContextIds: previous.terminalContextIds,
    };
    selectionRangeRef.current = { start: normalizedExpandedCursor, end: normalizedExpandedCursor };
    input.setIsEmpty(value.length === 0);
    // Callers decide when a reapply is worth it, so the selection is placed
    // on every explicit call (an unfocused editor keeps the caret for the
    // next focus, matching the Lexical fallback behavior).
    isApplyingControlledUpdate.current = true;
    const map = serializeEditorDoc(editor.state.doc);
    const flat = collapsedToFlat(map, normalizedCursor);
    editor.commands.setTextSelection(flatToPm(map, flat));
    editor.view.dispatch(editor.state.tr.scrollIntoView());
    queueMicrotask(() => {
      isApplyingControlledUpdate.current = false;
    });
  };

  /** Rebuilds editor content while suppressing draft notifications through the next microtask. */
  const rebuild = (editor: Editor, content: Record<string, unknown>): void => {
    isApplyingControlledUpdate.current = true;
    editor.commands.setContent(content, { emitUpdate: false });
    queueMicrotask(() => {
      isApplyingControlledUpdate.current = false;
    });
  };

  const focusAt = (editor: Editor, nextCursor: number): void => {
    editor.view.dom.focus({ preventScroll: true });
    // A newer prompt is waiting to be applied (a chip was just inserted
    // through the store). Reporting the editor's stale text now would
    // overwrite that prompt; the pending rewrite places the caret instead.
    if (snapshotRef.current.value !== input.latestValue()) return;
    const boundedCursor = clampCollapsedComposerCursor(snapshotRef.current.value, nextCursor);
    const map = serializeEditorDoc(editor.state.doc);
    const flat = collapsedToFlat(map, boundedCursor);
    editor.commands.setTextSelection(flatToPm(map, flat));
    editor.view.dispatch(editor.state.tr.scrollIntoView());
    // Forge's focusAt always reports, even for an unchanged cursor: the
    // orchestrator rebinds trigger and pending state off this notification.
    snapshotRef.current = {
      value: snapshotRef.current.value,
      cursor: boundedCursor,
      expandedCursor: expandCollapsedComposerCursor(snapshotRef.current.value, boundedCursor),
      terminalContextIds: snapshotRef.current.terminalContextIds,
    };
    selectionRangeRef.current = {
      start: snapshotRef.current.expandedCursor,
      end: snapshotRef.current.expandedCursor,
    };
    input.onChange(
      snapshotRef.current.value,
      boundedCursor,
      snapshotRef.current.expandedCursor,
      false,
      snapshotRef.current.terminalContextIds,
    );
  };

  return {
    snapshotRef: snapshotRef as RefObject<ComposerSnapshot>,
    selectionRangeRef: selectionRangeRef as RefObject<{ start: number; end: number }>,
    syncFromEditor,
    readSnapshot,
    applyControlled,
    rebuild,
    focusAt,
  };
}
